import type { EventSink } from './events'
import { stepHazards } from './hazards/step'
import { ITEM, pressed } from './inputBits'
import type { InputBits } from './inputBits'
import { stepPickups } from './items/crate'
import { stepTrapsAndProjectiles } from './items/step'
import { stepItemUse } from './items/use'
import { updateCheckpoints, updateFinish, updateRanks } from './placement'
import { resolveTerrain } from './player/collide'
import { consumeInput } from './player/input'
import { integrateVelocity } from './player/move'
import { updateRespawn } from './player/respawn'
import { stepRideContacts } from './player/rideContacts'
import { updateStun } from './player/stun'
import { applySpeedStrips } from './terrain/zones'
import type { World } from './world'

/**
 * The only mutation entry point. Advances the world by one tick in the fixed order of
 * ARCHITECTURE §2.4. `inputs` is indexed by player index; a missing entry means no buttons.
 */
export function stepWorld(world: World, inputs: ReadonlyArray<InputBits>, events: EventSink): void {
  const n = world.players.length

  // 1. Input → intent. The ITEM edge is taken here (before `prevBits` moves on) for step 7.
  const bits: InputBits[] = []
  const itemPresses: boolean[] = []
  for (let i = 0; i < n; i++) {
    const prev = world.players[i]!.prevBits
    updateStun(world, i, events)
    bits.push(consumeInput(world, i, inputs[i] ?? 0, events))
    itemPresses.push(pressed(bits[i]!, prev, ITEM))
  }

  // 2. Velocities and gravity.
  const dys: number[] = []
  for (let i = 0; i < n; i++) dys.push(integrateVelocity(world, i, bits[i]!))

  // 3. Terrain collision and kinematic platforms; then riders act on other racers (D-050).
  const wasGrounded = world.players.map((p) => p.grounded)
  resolveTerrain(world, dys, events)
  stepRideContacts(world, wasGrounded, events)

  // 4. Hazards.
  stepHazards(world, events)

  // 5. Traps and projectiles.
  stepTrapsAndProjectiles(world, events)

  // 6. Pickups: crates and coins, speed strips, checkpoints.
  stepPickups(world, events)
  for (let i = 0; i < n; i++) {
    applySpeedStrips(world, i)
    updateCheckpoints(world, i, events)
  }

  // 7. Item use.
  stepItemUse(world, itemPresses, events)

  // 8. Finish, then pits and respawns.
  for (let i = 0; i < n; i++) {
    updateFinish(world, i, events)
    updateRespawn(world, i, events)
  }

  // 9. Rank, then advance the clock.
  updateRanks(world)
  world.tick++
}
